import { useMemo } from 'react';
import type { Event } from '@casetrace/shared';
import { useCaseEvents } from '../../hooks/use-case-events';
import { useTimelineSearch } from '../../hooks/use-timeline-search';
import { typesenseClient } from '../../lib/typesense';
import { eventHasAttachments } from './event-attachments';
import type { FilterState } from './filter-panel';

export type CardPosition = 'only' | 'first' | 'middle' | 'last';

export type DateHeaderRow = { kind: 'header'; dateKey: string; label: string };
export type EventRow = { kind: 'event'; event: Event; position: CardPosition };
export type LoadMoreRow = { kind: 'load-more' };
export type TimelineRow = DateHeaderRow | EventRow | LoadMoreRow;

function formatDateLabel(dateKey: string): string {
  const date = new Date(dateKey);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'I dag';
  if (diffDays === 1) return 'I går';
  return new Intl.DateTimeFormat('nb-NO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function matchesFilter(event: Event, filters: FilterState): boolean {
  if (filters.types.size > 0 && !filters.types.has(event.type)) return false;
  if (filters.hasAttachments && !eventHasAttachments(event)) return false;
  if (filters.dateFrom && event.timestamp < filters.dateFrom) return false;
  if (filters.dateTo && event.timestamp > `${filters.dateTo}T23:59:59`) return false;

  const fromEmail = String((event.metadata.from as { email?: string } | undefined)?.email ?? '')
    .trim()
    .toLowerCase();
  const textHaystack = `${event.title}\n${event.contentPreview ?? ''}`.toLowerCase();

  const includeMatches = filters.include.every((condition) =>
    matchesCondition(condition, fromEmail, textHaystack),
  );
  if (!includeMatches) return false;

  const excludeMatches = filters.exclude.some((condition) =>
    matchesCondition(condition, fromEmail, textHaystack),
  );
  if (excludeMatches) return false;

  return true;
}

function matchesCondition(
  condition: FilterState['include'][number],
  fromEmail: string,
  textHaystack: string,
): boolean {
  const value = condition.value.trim().toLowerCase();
  if (!value) return true;
  return condition.field === 'sender' ? fromEmail === value : textHaystack.includes(value);
}

/** Sørg for at deep-linket hendelse finnes i listen (kronologisk), uten duplikater. */
function mergeFocusEvent(events: Event[], focusEvent: Event | null): Event[] {
  if (!focusEvent) return events;
  if (events.some((event) => event.id === focusEvent.id)) return events;
  return [...events, focusEvent].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

function filterEventsForTimeline(
  events: Event[],
  filters: FilterState,
  highlightedEventIds: ReadonlySet<string>,
  shouldApplyInMemoryFilter: boolean,
  focusEventId: string | null,
): Event[] {
  let result = events;
  if (shouldApplyInMemoryFilter) {
    result = result.filter((event) => event.id === focusEventId || matchesFilter(event, filters));
  }
  if (filters.onlyStarred) {
    result = result.filter(
      (event) => event.id === focusEventId || highlightedEventIds.has(event.id),
    );
  }
  return result;
}

function buildRows(
  events: Event[],
  filters: FilterState,
  highlightedEventIds: ReadonlySet<string>,
  hasNextPage: boolean,
  shouldApplyInMemoryFilter: boolean,
  focusEventId: string | null,
): TimelineRow[] {
  const filtered = filterEventsForTimeline(
    events,
    filters,
    highlightedEventIds,
    shouldApplyInMemoryFilter,
    focusEventId,
  );
  const rows: TimelineRow[] = [];
  let lastDateKey = '';

  for (const event of filtered) {
    const dateKey = event.timestamp.slice(0, 10);
    if (dateKey !== lastDateKey) {
      rows.push({ kind: 'header', dateKey, label: formatDateLabel(dateKey) });
      lastDateKey = dateKey;
    }
    rows.push({ kind: 'event', event, position: 'only' });
  }

  if (hasNextPage) rows.push({ kind: 'load-more' });

  let i = 0;
  while (i < rows.length) {
    if (rows[i].kind !== 'event') {
      i++;
      continue;
    }
    let j = i;
    while (j < rows.length && rows[j].kind === 'event') j++;
    const count = j - i;
    if (count > 1) {
      (rows[i] as EventRow).position = 'first';
      for (let k = i + 1; k < j - 1; k++) (rows[k] as EventRow).position = 'middle';
      (rows[j - 1] as EventRow).position = 'last';
    }
    i = j;
  }

  return rows;
}

export function useCaseTimeline(
  caseId: string,
  filters: FilterState,
  highlightedEventIds: ReadonlySet<string> = new Set(),
  focusEvent: Event | null = null,
) {
  const hasSearchConditions =
    filters.include.length > 0 || filters.exclude.length > 0 || filters.hasAttachments;
  const isSearchMode = hasSearchConditions && Boolean(typesenseClient);
  const eventsQuery = useCaseEvents(caseId, !isSearchMode);
  const searchQuery = useTimelineSearch(caseId, filters, isSearchMode);
  const activeQuery = isSearchMode ? searchQuery : eventsQuery;
  const focusEventId = focusEvent?.id ?? null;

  const allEvents = useMemo(() => {
    const loaded = activeQuery.data?.pages.flatMap((p) => p.events) ?? [];
    return mergeFocusEvent(loaded, focusEvent);
  }, [activeQuery.data, focusEvent]);

  const rows = useMemo(
    () =>
      buildRows(
        allEvents,
        filters,
        highlightedEventIds,
        Boolean(activeQuery.hasNextPage),
        !isSearchMode,
        focusEventId,
      ),
    [allEvents, filters, highlightedEventIds, activeQuery.hasNextPage, isSearchMode, focusEventId],
  );

  const dateMin = allEvents.at(0)?.timestamp.slice(0, 10) ?? '';
  const dateMax = allEvents.at(-1)?.timestamp.slice(0, 10) ?? '';

  return {
    rows,
    isLoading: activeQuery.isLoading,
    isFetchingNextPage: activeQuery.isFetchingNextPage,
    hasNextPage: Boolean(activeQuery.hasNextPage),
    fetchNextPage: activeQuery.fetchNextPage,
    isSearchMode,
    dateMin,
    dateMax,
  };
}
